// modules/auth/views/ForgotPasswordView.tsx
import React, { useState } from 'react';
import { Form, Input, Button, Card, message, Modal, Typography, Image, Alert } from 'antd';
import { useTheme } from '@/components/common/ThemeProvider';
import { useForgotPassword } from "@/hooks/auth/use-forgot-password";
import { useNavigate, Link } from "react-router-dom";
import { useCompany } from "@/hooks/company/use-company";
const companyLogo = '/images/login.png';
import {useGetLoginLogo} from "@/shared/api/client/appearance-controller";
const defaultCompanyLogo = '/images/hazina-logo.jpg';

const { Title } = Typography;

const ForgotPasswordView: React.FC = () => {
    const [form] = Form.useForm();
    const { dark } = useTheme();
    const navigate = useNavigate();
    const { isLoading, data: companyData } = useCompany();

    const { 
        initiatePasswordReset, 
        verifyOtp, 
        resetPassword, 
        resendOtp,
        isOtpRequired, 
        maskedPhoneNumber, 
        resetToken,
        canResend,
        countdown,
        isResending
    } = useForgotPassword();
    const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);
    const [isResetModalOpen, setIsResetModalOpen] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [otpVerified, setOtpVerified] = useState(false);

    const { data: logoData, isLoading: logoLoading } = useGetLoginLogo();
    const logoSrc = typeof logoData === 'string' ? logoData : logoData?.data || defaultCompanyLogo;

    const handleInitiateReset = async (values: { email: string; phoneNumber: string }) => {
        setIsProcessing(true);
        setIsOtpModalOpen(false);
        setIsResetModalOpen(false);
        setOtpVerified(false);
        try {
            const response = await initiatePasswordReset(values);
            const requiresOtp = response?.otpRequired ?? false;
            const token = response?.resetToken ?? '';

            if (!token) {
                throw new Error('The password reset session could not be created. Please try again.');
            }

            if (requiresOtp) {
                setIsOtpModalOpen(true);
                message.success('OTP has been sent to your phone number.');
            } else {
                setIsResetModalOpen(true);
                message.success('Password reset initiated. You can now set your new password.');
            }
        } catch (error: any) {
            // Extract specific error message from backend response
            const errorMessage = error?.response?.data?.message || 
                               error?.response?.data?.error || 
                               error?.message || 
                               'Failed to initiate password reset. Please check your email and phone number.';
            message.error(errorMessage);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleVerifyOtp = async (values: { otp: string }) => {
        if (!resetToken) {
            message.error('Your password reset session has expired. Please start again.');
            setIsOtpModalOpen(false);
            return;
        }

        setIsProcessing(true);
        try {
            const isVerified = await verifyOtp({ resetToken, otp: values.otp });
            if (isVerified) {
                setOtpVerified(true);
                setIsOtpModalOpen(false);
                setIsResetModalOpen(true);
                message.success('OTP verified successfully. You can now set your new password.');
            } else {
                setOtpVerified(false);
                message.error('Invalid OTP. Please check and try again.');
            }
        } catch (error: any) {
            // Extract specific error message from backend response
            const errorMessage = error?.response?.data?.message || 
                               error?.response?.data?.error || 
                               error?.message || 
                               'Failed to verify OTP. Please try again.';
            message.error(errorMessage);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleResetPassword = async (values: { newPassword: string; confirmPassword: string }) => {
        // Security check: Ensure OTP is verified if required
        if (isOtpRequired && !otpVerified) {
            message.error('Please verify your OTP first.');
            setIsResetModalOpen(false);
            setIsOtpModalOpen(true);
            return;
        }

        if (values.newPassword !== values.confirmPassword) {
            message.error('Passwords do not match.');
            return;
        }

        if (values.newPassword.length < 6) {
            message.error('Password must be at least 6 characters long.');
            return;
        }

        setIsProcessing(true);
        try {
            await resetPassword({ resetToken, newPassword: values.newPassword });
            message.success('Password reset successfully! Redirecting to login...');
            setIsResetModalOpen(false);
            
            // Add a small delay for better UX
            setTimeout(() => {
                navigate("/login");
            }, 1500);
        } catch (error: any) {
            // Extract specific error message from backend response
            const errorMessage = error?.response?.data?.message || 
                               error?.response?.data?.error || 
                               error?.message || 
                               'Failed to reset password. Please try again.';
            message.error(errorMessage);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleCancelOtp = () => {
        setIsOtpModalOpen(false);
        setOtpVerified(false);
        message.info('Password reset cancelled. You can try again.');
    };

    const handleCancelReset = () => {
        setIsResetModalOpen(false);
        setOtpVerified(false);
        message.info('Password reset cancelled. You can try again.');
    };

    const handleResendOtp = async () => {
        try {
            await resendOtp();
            message.success('New OTP has been sent to your phone number.');
        } catch (error: any) {
            // Extract specific error message from backend response
            const errorMessage = error?.response?.data?.message || 
                               error?.response?.data?.error || 
                               error?.message || 
                               'Failed to resend OTP. Please try again.';
            message.error(errorMessage);
        }
    };

    return (
        <div className={`min-h-screen flex items-center justify-center ${dark ? 'bg-gray-900' : 'bg-gray-100'}`}>
            <Card className={`w-full max-w-md ${dark ? 'bg-gray-800 text-white' : ''}`}>
                <div className="flex flex-col items-center mb-6">
                    <Image src={logoSrc} alt="Company Logo" preview={false} width={100}/>
                    <Title level={3} className={`mt-4 ${dark ? 'text-white' : 'text-gray-800'}`}>
                        {isLoading ? 'Loading...' : companyData?.brFname}
                    </Title>
                </div>
                
                <h2 className={`text-2xl text-center font-bold mb-4 ${dark ? 'text-white' : 'text-gray-800'}`}>
                    Reset Password
                </h2>
                
                <Alert 
                    message="Password Reset Instructions" 
                    description="Enter your email and phone number to receive a password reset link or OTP." 
                    type="info" 
                    showIcon 
                    className="mb-4"
                />
                
                <Form form={form} onFinish={handleInitiateReset} layout="vertical">
                    <Form.Item
                        name="email"
                        label="Email"
                        rules={[
                            { required: true, type: 'email', message: 'Please input a valid email!' },
                            { max: 100, message: 'Email must be less than 100 characters!' }
                        ]}
                    >
                        <Input placeholder="Enter your email address" />
                    </Form.Item>
                    
                    <Form.Item
                        name="phoneNumber"
                        label="Phone Number"
                        rules={[
                            { required: true, message: 'Please input your phone number!' },
                            { pattern: /^[+]?[\d\s\-()]+$/, message: 'Please enter a valid phone number!' }
                        ]}
                    >
                        <Input placeholder="Enter your phone number" />
                    </Form.Item>
                    
                    <Form.Item>
                        <Button 
                            type="primary" 
                            htmlType="submit" 
                            className="w-full" 
                            loading={isProcessing}
                            disabled={isProcessing}
                        >
                            {isProcessing ? 'Processing...' : 'Reset Password'}
                        </Button>
                    </Form.Item>
                </Form>

                <div className="mt-4 text-center">
                    <Link 
                        to="/login"
                        className={`font-medium ${dark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-500'}`}
                    >
                        ← Back to Login
                    </Link>
                </div>
            </Card>

            {/* OTP Verification Modal */}
            <Modal
                title="Verify OTP"
                open={isOtpModalOpen}
                onCancel={handleCancelOtp}
                footer={null}
                maskClosable={false}
            >
                <Alert 
                    message={`OTP sent to phone number ending in ${maskedPhoneNumber}`}
                    type="success" 
                    showIcon 
                    className="mb-4"
                />
                
                <Form onFinish={handleVerifyOtp} layout="vertical">
                    <Form.Item
                        name="otp"
                        label="Enter OTP"
                        rules={[
                            { required: true, message: 'Please input the OTP!' },
                            { len: 6, message: 'OTP must be 6 digits!' }
                        ]}
                    >
                        <Input 
                            placeholder="Enter 6-digit OTP" 
                            maxLength={6}
                            style={{ textAlign: 'center', fontSize: '18px', letterSpacing: '4px' }}
                        />
                    </Form.Item>
                    
                    <Form.Item className="mb-0">
                        <div className="flex gap-2 mb-3">
                            <Button 
                                type="primary" 
                                htmlType="submit" 
                                loading={isProcessing}
                                disabled={isProcessing}
                                className="flex-1"
                            >
                                {isProcessing ? 'Verifying...' : 'Verify OTP'}
                            </Button>
                            <Button onClick={handleCancelOtp} disabled={isProcessing}>
                                Cancel
                            </Button>
                        </div>
                        
                        {/* Resend OTP Section */}
                        <div className="text-center pt-2 border-t">
                            {canResend ? (
                                <Button 
                                    type="link" 
                                    onClick={handleResendOtp}
                                    loading={isResending}
                                    disabled={isResending || isProcessing}
                                    className="p-0"
                                >
                                    {isResending ? 'Sending...' : 'Resend OTP'}
                                </Button>
                            ) : (
                                <span className="text-gray-500 text-sm">
                                    Resend OTP in {countdown}s
                                </span>
                            )}
                        </div>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Password Reset Modal */}
            <Modal
                title="Set New Password"
                open={isResetModalOpen && (!isOtpRequired || otpVerified)}
                onCancel={handleCancelReset}
                footer={null}
                maskClosable={false}
            >
                <Alert 
                    message="Create a strong password"
                    description="Your password should be at least 6 characters long and contain a mix of letters, numbers, and special characters."
                    type="info" 
                    showIcon 
                    className="mb-4"
                />
                
                <Form onFinish={handleResetPassword} layout="vertical">
                    <Form.Item
                        name="newPassword"
                        label="New Password"
                        rules={[
                            { required: true, message: 'Please input your new password!' },
                            { min: 6, message: 'Password must be at least 6 characters long!' }
                        ]}
                    >
                        <Input.Password placeholder="Enter new password" />
                    </Form.Item>
                    
                    <Form.Item
                        name="confirmPassword"
                        label="Confirm Password"
                        dependencies={['newPassword']}
                        rules={[
                            { required: true, message: 'Please confirm your new password!' },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('newPassword') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('Passwords do not match!'));
                                },
                            }),
                        ]}
                    >
                        <Input.Password placeholder="Confirm new password" />
                    </Form.Item>
                    
                    <Form.Item className="mb-0">
                        <div className="flex gap-2">
                            <Button 
                                type="primary" 
                                htmlType="submit" 
                                loading={isProcessing}
                                disabled={isProcessing}
                                className="flex-1"
                            >
                                {isProcessing ? 'Resetting...' : 'Reset Password'}
                            </Button>
                            <Button onClick={handleCancelReset} disabled={isProcessing}>
                                Cancel
                            </Button>
                        </div>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ForgotPasswordView;
